import { apiRequest } from "./api";

const requiredBeginParameterNames = Object.freeze([
  "client_id",
  "redirect_uri",
  "response_type",
  "scope",
  "state",
  "nonce",
  "code_challenge",
  "code_challenge_method",
]);
const optionalBeginParameterNames = Object.freeze(["prompt"]);
const resumeParameterName = "onboarding_transaction_id";
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const previewStoragePrefix = "orion_oauth_onboarding_preview_v1:";
const supportedAuthMethods = Object.freeze(["password", "sms", "wechat"]);

export class OnboardingLocationError extends Error {
  constructor(message) {
    super(message);
    this.name = "OnboardingLocationError";
    this.code = "invalid_authorization_request";
  }
}

function singleNonEmptyValue(params, name) {
  const values = params.getAll(name);
  return values.length === 1 && values[0] !== "";
}

export function parseAuthorizationSearch(search = "") {
  const params = new URLSearchParams(String(search || "").replace(/^\?/, ""));
  const names = Array.from(params.keys());
  if (
    names.length === 1
    && names[0] === resumeParameterName
    && singleNonEmptyValue(params, resumeParameterName)
  ) {
    const transactionId = params.get(resumeParameterName);
    if (!uuidPattern.test(transactionId)) {
      throw new OnboardingLocationError("授权事务标识无效");
    }
    return { mode: "resume", transactionId };
  }

  if (
    names.some((name) => !requiredBeginParameterNames.includes(name) && !optionalBeginParameterNames.includes(name))
    || requiredBeginParameterNames.some((name) => !singleNonEmptyValue(params, name))
    || (params.has("prompt") && (!singleNonEmptyValue(params, "prompt") || params.get("prompt") !== "none"))
  ) {
    throw new OnboardingLocationError("授权请求参数不完整或包含未允许的字段");
  }
  return { mode: "begin", query: params.toString() };
}

export function normalizeAllowedAuthMethods(methods, fallback = []) {
  const source = Array.isArray(methods) ? methods : fallback;
  return supportedAuthMethods.filter((method) => source.includes(method));
}

export function usableTenantOptions(tenants) {
  return (Array.isArray(tenants) ? tenants : []).filter(
    (tenant) => tenant && (tenant.can_install === true || tenant.can_authorize === true),
  );
}

export function resolveTenantSelection(currentTenantId, tenants) {
  const usable = usableTenantOptions(tenants);
  if (usable.some((tenant) => tenant.id === currentTenantId)) return currentTenantId;
  return usable.length === 1 ? usable[0].id : "";
}

function transactionPath(transactionId, suffix = "") {
  if (!uuidPattern.test(String(transactionId || ""))) {
    throw new OnboardingLocationError("授权事务标识无效");
  }
  return `/api/v1/platform/onboarding/${encodeURIComponent(transactionId)}${suffix}`;
}

export function beginAuthorization(query) {
  return apiRequest(`/oauth2/authorize?${query}`);
}

export function resumeAuthorization(transactionId) {
  const query = new URLSearchParams({ [resumeParameterName]: transactionId });
  return apiRequest(`/oauth2/authorize?${query.toString()}`);
}

export function getOnboardingTransaction(transactionId) {
  return apiRequest(transactionPath(transactionId));
}

export function selectOnboardingTenant(transactionId, tenantId) {
  return apiRequest(transactionPath(transactionId, "/tenant"), {
    method: "POST",
    body: JSON.stringify({ tenant_id: tenantId }),
  });
}

export function createOnboardingTenant(transactionId, name) {
  return apiRequest(transactionPath(transactionId, "/tenant/create"), {
    method: "POST",
    body: JSON.stringify({ name }),
  });
}

export function installOnboardingApplication(transactionId) {
  return apiRequest(transactionPath(transactionId, "/install"), {
    method: "POST",
    body: JSON.stringify({ consent: true }),
  });
}

export function completeOnboarding(transactionId) {
  return apiRequest(transactionPath(transactionId, "/complete"), {
    method: "POST",
  });
}

export function failSilentOnboarding(transactionId) {
  return apiRequest(transactionPath(transactionId, "/silent"), {
    method: "POST",
  });
}

function previewStorageKey(transactionId) {
  return `${previewStoragePrefix}${transactionId}`;
}

export function saveOnboardingPreview(transaction, storage = window.sessionStorage) {
  if (!transaction || !uuidPattern.test(String(transaction.id || ""))) {
    return;
  }
  const preview = {
    id: transaction.id,
    application: transaction.application,
    requested_scopes: Array.isArray(transaction.requested_scopes) ? transaction.requested_scopes : [],
    resource_scopes: Array.isArray(transaction.resource_scopes) ? transaction.resource_scopes : [],
    expires_at: transaction.expires_at,
    prompt: transaction.prompt === "none" ? "none" : "",
  };
  storage.setItem(previewStorageKey(transaction.id), JSON.stringify(preview));
}

export function loadOnboardingPreview(transactionId, storage = window.sessionStorage) {
  if (!uuidPattern.test(String(transactionId || ""))) {
    return null;
  }
  let parsed;
  try {
    parsed = JSON.parse(storage.getItem(previewStorageKey(transactionId)) || "null");
  } catch (error) {
    return null;
  }
  if (
    !parsed
    || parsed.id !== transactionId
    || !parsed.application
    || !Array.isArray(parsed.application.allowed_auth_methods)
  ) {
    return null;
  }
  return parsed;
}

export function clearOnboardingPreview(transactionId, storage = window.sessionStorage) {
  if (uuidPattern.test(String(transactionId || ""))) {
    storage.removeItem(previewStorageKey(transactionId));
  }
}

function isLoopbackHostname(hostname) {
  const normalized = String(hostname || "").toLowerCase();
  return normalized === "localhost" || normalized === "::1" || /^127(?:\.[0-9]{1,3}){3}$/.test(normalized);
}

export function resolveCompletionRedirect(payload) {
  const raw = payload && typeof payload.redirect_uri === "string" ? payload.redirect_uri : "";
  let parsed;
  try {
    parsed = new URL(raw);
  } catch (error) {
    throw new Error("授权回调地址无效");
  }
  const secure = parsed.protocol === "https:";
  const loopback = parsed.protocol === "http:" && isLoopbackHostname(parsed.hostname);
  if (
    (!secure && !loopback)
    || parsed.username
    || parsed.password
    || parsed.hash
    || !parsed.searchParams.get("code")
    || !parsed.searchParams.get("state")
  ) {
    throw new Error("授权回调地址无效");
  }
  return parsed.toString();
}

export function navigateToCompletion(payload, location = window.location) {
  const redirectURI = resolveCompletionRedirect(payload);
  location.assign(redirectURI);
  return redirectURI;
}

export function resolveSilentFailureRedirect(payload) {
  const raw = payload && typeof payload.redirect_uri === "string" ? payload.redirect_uri : "";
  let parsed;
  try {
    parsed = new URL(raw);
  } catch (error) {
    throw new Error("授权回调地址无效");
  }
  const secure = parsed.protocol === "https:";
  const loopback = parsed.protocol === "http:" && isLoopbackHostname(parsed.hostname);
  if (
    (!secure && !loopback)
    || parsed.username
    || parsed.password
    || parsed.hash
    || !["login_required", "interaction_required"].includes(parsed.searchParams.get("error"))
    || !parsed.searchParams.get("state")
    || parsed.searchParams.has("code")
  ) {
    throw new Error("授权回调地址无效");
  }
  return parsed.toString();
}

export function navigateToSilentFailure(payload, location = window.location) {
  const redirectURI = resolveSilentFailureRedirect(payload);
  location.assign(redirectURI);
  return redirectURI;
}

export async function redirectSilentOnboardingFailure(
  { transaction = null, locationMode = null } = {},
  { fail = failSilentOnboarding, navigate = navigateToSilentFailure } = {},
) {
  const knownSilentTransaction = !!(
    transaction
    && transaction.prompt === "none"
    && uuidPattern.test(String(transaction.id || ""))
  );
  const resumableTransaction = !!(
    !transaction
    && locationMode
    && locationMode.mode === "resume"
    && uuidPattern.test(String(locationMode.transactionId || ""))
  );
  if (!knownSilentTransaction && !resumableTransaction) {
    return false;
  }

  const transactionId = knownSilentTransaction ? transaction.id : locationMode.transactionId;
  try {
    const payload = await fail(transactionId);
    navigate(payload);
    return true;
  } catch (error) {
    // A resume URL contains no prompt. Let the server decide whether its stored
    // transaction is silent, then preserve the normal interactive fallback.
    if (!knownSilentTransaction) {
      return false;
    }
    throw error;
  }
}

const scopeLabels = Object.freeze({
  openid: "确认 Orion 平台身份",
  profile: "读取名称和头像",
  email: "读取已授权的邮箱信息",
  offline_access: "在会话结束后继续按授权范围访问",
  "identity:read": "读取稳定的 Subject 身份标识",
  "memberships:read": "读取当前公司的成员投影",
  "memberships:invite": "邀请成员加入当前公司空间",
  "ai:models.read": "查询可用 AI 模型",
  "ai:runs.read": "读取 AI Run 状态和结果",
  "ai:runs.write": "创建、取消和重试 AI Run",
  "ai:assets.read": "读取授权范围内的 AI Asset",
  "ai:assets.write": "上传和管理 AI Asset",
  "ai:usage.read": "读取 AI 用量记录",
  "ai:events.read": "读取 AI 执行事件",
});

export function describeScope(scope) {
  return scopeLabels[scope] || scope;
}
